import React, { FunctionComponent, useEffect, useState } from 'react';
import { cloneDeep, startCase } from 'lodash';
import { CrmType } from '@upstart/harmony/lib/constants';
import Zoom from '../../Zoom';
import eleadUserCreationImage from '../../../assets/guides/crm-usercreation-elead.png';
import dealersocketUserCreationMenuImage from '../../../assets/guides/crm-usercreation-dealersocket-menu.png';
import dealersocketUserCreationNewUserImage from '../../../assets/guides/crm-usercreation-dealersocket-newuser.png';
import dealersocketUserCreationUserTypeImage from '../../../assets/guides/crm-usercreation-dealersocket-usertype.png';
import vinsolutionsUserCreationUserInfoImage from '../../../assets/guides/crm-usercreation-vinsolutions-userinfo.png';
import vinsolutionsUserCreationAdvSettingsImage from '../../../assets/guides/crm-usercreation-vinsolutions-advsettings.png';
import vinsolutionsIDImage from '../../../assets/guides/crm-vinsolutions-id.png';
import { DealerLaunchStepProps } from '../types';
import { StepInfoGateProps } from './DealerLaunchInfoGate';
import { Dealer } from '../../../types/dealerTypes';
import { Select } from '../../Input/Select/Select';
import { TextField } from '../../Input/TextField/TextField';
import { Login } from '../../../types/loginTypes';
import CRMLoginsService from '../../../services/CRMLoginsService';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import {
  wholeNumberValidator,
  triggerValidation,
} from '../../../utils/validation';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { buildModalBreadcrumbs, QuickGuide } from '../../QuickGuide/QuickGuide';
import { addCRMDefaults } from '../defaults';
import { CRM_TYPE_NAME } from '../../../utils/constants';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

export const DealerLaunchIntegrationCRMInfoGate: StepInfoGateProps = {
  title: "Now, let's get your CRM Connected",
  cardTitle: 'Before we connect your CRM',
  cardDescription: (
    <p>
      We care about protecting your information. Throughout this process, we
      will need access to things like your CRM. This is always so that our
      service can seamlessly integrate with your business.
      <br />
      <br />
      Having access to your CRM will allow Upstart Auto Retail to push and pull
      lead information in the online and in-store platform. Upstart Auto Retail
      will keep your information safe throughout this partnership and will not
      delete any information. If you haven&apos;t already, please make Upstart
      Auto Retail a login to your CRM with admin access to continue the process.
      <br />
      <br />
    </p>
  ),
  continueText: 'I Understand',
};

interface FormData {
  dealer: Dealer;
  login?: { username: string; password: string; confirmPassword?: string };
}

export const DealerLaunchIntegrationCRM: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<FormData>({ defaultValues: { dealer } });
  const { dirtyFields } = form.formState; // due to react-hook-form's use of proxy, it's important to retrieve this value during render
  const crmType = form.watch('dealer.crmType');
  const loginPassword = form.watch('login.password');
  const [error, setError] = useState<unknown>();
  const hasExistingLogin = !!dealer.launchpad?.crm?.primaryLoginId;

  // if an existing login is present, trigger validation on the login fields to indicate that they're filled
  useEffect(() => {
    if (hasExistingLogin) {
      triggerValidation(form, 'login.username');
      triggerValidation(form, 'login.password');
      triggerValidation(form, 'login.confirmPassword');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (update: FormData) => {
    setError(undefined);
    const dealerUpdate = cloneDeep(update.dealer);

    // if the login data changed, create an update
    let loginUpdate: Partial<Login> | undefined;
    if (update.login && dirtyFields.login) {
      loginUpdate = { login: update.login.username, isActive: true };
      if (update.login.password) {
        if (update.login.password !== update.login.confirmPassword) {
          form.setError('login.confirmPassword', {
            message: 'Password confirmation does not match',
          });
          return;
        }
        loginUpdate.password = update.login.password;
      }
    }

    // save the login
    if (loginUpdate) {
      let updatePromise;
      if (!hasExistingLogin) {
        // create a new login if there's no existing one
        updatePromise = CRMLoginsService.createLogin({
          dealer_id: dealer.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type: update.dealer.crmType!,
          ...loginUpdate,
        }).then((result) => {
          // save the id
          dealerUpdate.launchpad = dealerUpdate.launchpad || {};
          dealerUpdate.launchpad.crm = dealerUpdate.launchpad.crm || {};
          dealerUpdate.launchpad.crm.primaryLoginId = result.data.id;
          return result;
        });
      } else {
        updatePromise = CRMLoginsService.updateLogin(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dealer.launchpad!.crm!.primaryLoginId!,
          loginUpdate,
        );
      }
      const login = await updatePromise.catch(() => {
        setError('Failed to save login, please contact support for assistance');
        return null;
      });
      if (!login) return;
    }

    // save the dealer
    await nextStep(addCRMDefaults(dealerUpdate));
  };

  const getCrmTypes = () => {
    // Function to order crm types in the crm dropdown
    const IGNORED_CRMS = [CrmType.HIGHERGEAR, CrmType.STANDALONE];
    const filteredList = Object.values(CrmType).filter(
      (c) => !IGNORED_CRMS.includes(c),
    );
    filteredList.unshift(CrmType.STANDALONE);

    return filteredList.map((type) => ({
      label: CRM_TYPE_NAME[type] || startCase(type),
      value: type,
    }));
  };

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={DealerLaunchIntegrationCRMInfoGate.title}
      subTitle="We'll need access to your CRM to give you access to all your customer information. If you haven't already, please make a new admin user that we can use to access your CRM."
      cardTitle="Which CRM do you use?"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <ErrorMessage error={error} />
      <div className={styles.stepRow}>
        <Select
          label="CRM"
          tooltip="Your CRM is where your dealership manages all leads."
          name="dealer.crmType"
          width="md"
          form={form}
          validation={{ required: true }}
          options={getCrmTypes()}
          addEmptyOption
        />
        {!!crmType && crmType === CrmType.STANDALONE && (
          <TextField
            label="Other CRM Name"
            name="dealer.otherCrmName"
            width="lg"
            form={form}
            validation={{ required: true }}
          />
        )}
        {!!crmType && crmType !== CrmType.STANDALONE && (
          <TextField
            label="Admin Login"
            tooltip="Create a CRM login for Upstart Auto Retail with Admin access."
            name="login.username"
            width="md"
            form={form}
            validation={{ required: !hasExistingLogin }}
            placeholder={hasExistingLogin ? '********' : ''}
          />
        )}
      </div>
      {!!crmType && crmType !== CrmType.STANDALONE && (
        <div className={styles.stepRow}>
          <TextField
            label="Admin Password"
            tooltip="Create a memorable password that is different from the username."
            name="login.password"
            type="password"
            width="md"
            form={form}
            validation={{ required: !hasExistingLogin }}
            placeholder={hasExistingLogin ? '********' : ''}
          />
          <TextField
            label="Confirm Admin Password"
            name="login.confirmPassword"
            type="password"
            width="md"
            form={form}
            validation={{ required: !!loginPassword }}
            placeholder={hasExistingLogin && !loginPassword ? '********' : ''}
          />
        </div>
      )}
      {crmType === CrmType.VINSOLUTIONS && (
        <>
          <TextField
            label="VinSolutions ID"
            name="dealer.vinsolutions.vinsolutionsId"
            width="md"
            form={form}
            validation={{ required: true, pattern: wholeNumberValidator }}
          />
          <Checkbox
            label="I have enabled Upstart Auto Retail as a partner in VinSolutions"
            name="dealer.launchpad.crm.enabledVinSolutionsPartnerIntegration"
            form={form}
            validation={{ required: true }}
          />
        </>
      )}
      {!!crmType &&
        crmType !== CrmType.STANDALONE &&
        crmType !== CrmType.REYNOLDS && (
          <QuickGuide
            label="Not sure how to fill out this page?"
            action="Guide Me Through CRM Integration"
            guideTitle="CRM Integration"
            hasZoomedImage
          >
            <div className="modal-subtitle">Admin Login</div>

            {crmType === CrmType.ELEAD && (
              <div>
                In Elead, select
                {buildModalBreadcrumbs(
                  'Admin',
                  'CRM Setup',
                  'Personnel',
                  'Add Personnel',
                )}
                Select &quot;Administrator&quot; under Position.
                <Zoom>
                  <img
                    src={eleadUserCreationImage}
                    alt="Elead User Creation"
                    className="zoomable-image"
                  />
                </Zoom>
              </div>
            )}

            {crmType === CrmType.VINSOLUTIONS && (
              <div>
                In VinSolutions, select
                {buildModalBreadcrumbs('Settings', 'Users', 'User List', 'Add')}
                <Zoom>
                  <img
                    src={vinsolutionsUserCreationUserInfoImage}
                    alt="VinSolutions User Creation"
                    className="zoomable-image"
                  />
                </Zoom>
                Select &quot;Admin&quot; under
                {buildModalBreadcrumbs(
                  'Advanced Settings tab',
                  'ILM/CRM Settings',
                )}
                Leave Alerts off.
                <Zoom>
                  <img
                    src={vinsolutionsUserCreationAdvSettingsImage}
                    alt="VinSolutions User Advanced Settings"
                    className="zoomable-image"
                  />
                </Zoom>
                <br />
                <br />
                <div className="modal-subtitle">VinSolutions ID</div>
                Login to your VinSolutions account and find it on the top left
                of the screen (next to your dealership&apos;s name).
                <Zoom>
                  <img
                    src={vinsolutionsIDImage}
                    alt="VinSolutions ID"
                    className="zoomable-image"
                  />
                </Zoom>
                <br />
                <br />
                <div className="modal-subtitle">
                  Enabling Upstart Auto Retail as a partner
                </div>
                Upstart Auto Retail will also need to be a partner in your CRM.
                Please login to your CRM and click on
                {buildModalBreadcrumbs(
                  'Settings',
                  'Dealer Settings',
                  'Partner Enablement',
                )}
                Scroll down to Upstart Auto Retail and click &quot;Enable&quot;.
                Check the checkboxes and click &quot;I agree&quot;.
              </div>
            )}

            {crmType === CrmType.DEALERSOCKET && (
              <div>
                Login to DealerSocket
                <br />
                Click on the menu bar on the top right and click
                &quot;Admin&quot;.
                <Zoom>
                  <img
                    src={dealersocketUserCreationMenuImage}
                    alt="Dealer Socket Menu"
                    className="zoomable-image"
                  />
                </Zoom>
                <br />
                Click on &quot;Dealership Users&quot; and on the top right,
                click on &quot;New&quot;.
                <Zoom>
                  <img
                    src={dealersocketUserCreationNewUserImage}
                    alt="Dealer Socket New User"
                    className="zoomable-image"
                  />
                </Zoom>
                <br />
                Enter a first/last name, in the team field, click
                &quot;Administrator&quot;.
                <Zoom>
                  <img
                    src={dealersocketUserCreationUserTypeImage}
                    alt="Dealer Socket User Type"
                    className="zoomable-image"
                  />
                </Zoom>
              </div>
            )}
          </QuickGuide>
        )}
    </DealerLaunchFormCardLayout>
  );
};
